import moment from "moment";

export const DISPLAY_DATE_FORMAT = "DD.MM.YYYY";
const DISPLAY_DATE_RANGE_SEPARATOR = " - ";
const DATE_SLOT_COUNT = 8;

export function getFormattedDateDigits(raw) {
  return String(raw ?? "").replace(/\D/g, "").slice(0, DATE_SLOT_COUNT);
}

export function createEmptyDateSlots() {
  return Array(DATE_SLOT_COUNT).fill("");
}

function assignSegmentDigitsToSlots(slots, segment, startSlot, slotLength) {
  const digits = String(segment ?? "").replace(/\D/g, "").slice(0, slotLength).split("");

  if (slotLength === 2 && digits.length === 1) {
    slots[startSlot] = "";
    slots[startSlot + 1] = digits[0];
    return;
  }

  digits.forEach((digit, index) => {
    if (index < slotLength) {
      slots[startSlot + index] = digit;
    }
  });
}

/** Parse display value into fixed DD|MM|YYYY slots (digits do not shift between segments). */
export function formattedToDateSlots(formatted) {
  const slots = createEmptyDateSlots();
  const value = String(formatted ?? "").trim();

  if (!value) {
    return slots;
  }

  const [dayPart = "", monthPart = "", yearPart = ""] = value.split(".");

  assignSegmentDigitsToSlots(slots, dayPart, 0, 2);
  assignSegmentDigitsToSlots(slots, monthPart, 2, 2);
  assignSegmentDigitsToSlots(slots, yearPart, 4, 4);

  return slots;
}

export function dateSlotsToFormatted(slots) {
  const dayPart = `${slots[0] || ""}${slots[1] || ""}`;
  const monthPart = `${slots[2] || ""}${slots[3] || ""}`;
  const yearPart = slots.slice(4).join("");
  const parts = [];

  if (dayPart || monthPart || yearPart) {
    parts.push(dayPart);
  }
  if (monthPart || yearPart) {
    parts.push(monthPart);
  }
  if (yearPart) {
    parts.push(yearPart);
  }

  return parts.join(".");
}

export function countFilledDateSlots(slots) {
  return slots.filter(Boolean).length;
}

export function areDateSlotsComplete(slots) {
  return slots.every(Boolean);
}

export function formatDigitsToDisplayDate(digitsRaw) {
  const digits = getFormattedDateDigits(digitsRaw);
  const slots = createEmptyDateSlots();

  digits.split("").forEach((digit, index) => {
    if (index < DATE_SLOT_COUNT) {
      slots[index] = digit;
    }
  });

  return dateSlotsToFormatted(slots);
}

export function getDigitIndexBeforeCursor(value, cursor) {
  if (cursor == null || cursor <= 0) {
    return 0;
  }

  return String(value).slice(0, cursor).replace(/\D/g, "").length;
}

export function getCursorAtDigitIndex(formattedValue, digitIndex) {
  const value = String(formattedValue ?? "");

  if (digitIndex <= 0) {
    return 0;
  }

  let digitsSeen = 0;
  for (let i = 0; i < value.length; i++) {
    if (/\d/.test(value[i])) {
      if (digitsSeen === digitIndex) {
        return i;
      }
      digitsSeen++;
    }
  }

  return value.length;
}

/** Edit fixed DD|MM|YYYY slots without moving month/year into day positions. */
export function applyDateSlotEdit(
  previousFormatted,
  { startSlotIndex, endSlotIndex, insertDigits = "" },
) {
  const slots = formattedToDateSlots(previousFormatted);
  const insert = getFormattedDateDigits(insertDigits).split("");
  const start = Math.max(0, Math.min(startSlotIndex, DATE_SLOT_COUNT));
  const end = Math.max(start, Math.min(endSlotIndex, DATE_SLOT_COUNT));

  for (let index = start; index < end; index++) {
    slots[index] = "";
  }

  let writeIndex = start;
  insert.forEach((digit) => {
    if (writeIndex >= DATE_SLOT_COUNT) {
      return;
    }
    slots[writeIndex] = digit;
    writeIndex++;
  });

  return dateSlotsToFormatted(slots);
}

/** @deprecated Use applyDateSlotEdit — kept for compatibility with range sanitizers. */
export function applyFormattedDateDigitEdit(
  previousFormatted,
  { startDigitIndex, endDigitIndex, insertDigits = "" },
) {
  return applyDateSlotEdit(previousFormatted, {
    startSlotIndex: startDigitIndex,
    endSlotIndex: endDigitIndex,
    insertDigits,
  });
}

/** Restrict manual typing to DD.MM.YYYY (max 8 digits). */
export function sanitizeDisplayDateInput(raw) {
  if (raw == null) {
    return "";
  }

  return formatDigitsToDisplayDate(raw);
}

/** Restrict manual range typing to `DD.MM.YYYY - DD.MM.YYYY`. */
export function sanitizeDisplayDateRangeInput(raw) {
  if (raw == null) {
    return "";
  }

  const str = String(raw);
  const sepIndex = str.indexOf(DISPLAY_DATE_RANGE_SEPARATOR);

  if (sepIndex === -1) {
    const first = sanitizeDisplayDateInput(str);

    if (first.length === DISPLAY_DATE_FORMAT.length) {
      const tail = str.slice(first.length).replace(/^[\s-]+/, "");
      if (tail.length > 0) {
        return `${first}${DISPLAY_DATE_RANGE_SEPARATOR}${sanitizeDisplayDateInput(tail)}`;
      }
    }

    return first;
  }

  const first = sanitizeDisplayDateInput(str.slice(0, sepIndex));
  const second = sanitizeDisplayDateInput(
    str.slice(sepIndex + DISPLAY_DATE_RANGE_SEPARATOR.length),
  );

  return `${first}${DISPLAY_DATE_RANGE_SEPARATOR}${second}`;
}

export function parseDisplayDateStrict(value) {
  const parsed = moment(value, DISPLAY_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
}

function readSegmentNumber(segment, { min, max }) {
  if (!segment) {
    return null;
  }

  const parsed = Number.parseInt(segment, 10);
  if (Number.isNaN(parsed) || parsed < min || parsed > max) {
    return null;
  }

  return parsed;
}

/** Keep calendar month/day/year in sync while the user edits individual slots. */
export function resolveDisplayDateFromSlots(slots, fallbackDate = null) {
  if (areDateSlotsComplete(slots)) {
    const strictDate = parseDisplayDateStrict(dateSlotsToFormatted(slots));
    if (strictDate) {
      return strictDate;
    }
  }

  const fallback = fallbackDate ? moment(fallbackDate) : null;
  let day = fallback?.date() ?? 1;
  let month = fallback?.month() ?? 0;
  let year = fallback?.year() ?? moment().year();

  const daySegment = `${slots[0] || ""}${slots[1] || ""}`;
  const monthSegment = `${slots[2] || ""}${slots[3] || ""}`;
  const yearSegment = slots.slice(4).join("");

  const parsedDay = readSegmentNumber(daySegment, { min: 1, max: 31 });
  const parsedMonth = readSegmentNumber(monthSegment, { min: 1, max: 12 });
  const parsedYear = readSegmentNumber(yearSegment, { min: 1, max: 9999 });

  if (parsedDay != null) {
    day = parsedDay;
  }
  if (parsedMonth != null) {
    month = parsedMonth - 1;
  }
  if (parsedYear != null && yearSegment.length === 4) {
    year = parsedYear;
  }

  const maxDayInMonth = moment({ year, month }).daysInMonth();
  const candidate = moment({
    year,
    month,
    day: Math.min(day, maxDayInMonth),
  });

  return candidate.isValid() ? candidate.toDate() : (fallbackDate ?? null);
}

export function formatDate(d) {
  if (!d) return ""; // Protection against null/undefined
  return moment(d, ["DD.MM.YYYY", moment.ISO_8601]).format("DD.MM.YYYY");
}

export function formatMonth(d) {
  return moment(d).format("MMM - YYYY");
}

export function formatDateWithMonth(d) {
  if (!d) return ""; // Protection against null/undefined
  return moment(d).format("DD MMM YYYY");
}

export function formatTime(d) {
  return moment(d).format("HH:mm");
}

export function formatDateForServer(d) {
  return moment(d).format("YYYY-MM-DD");
}

export function formatDateForInvoice(d) {
  if (!d) return "";
  return moment(d).format("DD.MM.YYYY");
}

export function formatDateForHistory(d) {
  if (!d) return "";
  return moment(d).format("DD/MM/YYYY hh:mm");
}

export function formatDateTime24(d) {
  if (!d) return "";
  return moment(d, ["DD.MM.YYYY", moment.ISO_8601]).format("DD.MM.YYYY HH:mm");
}